/**
 * TanStack Query hooks wrapping the typed API client. Each hook owns its query
 * key + fetcher; mutations invalidate the relevant keys on success.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  ConversationListItem,
  CouriersResponse,
  DashboardStats,
  EmbeddedSignupResult,
  MessageTemplate,
  OrderListItem,
  OrderStatus,
  QrConnectionState,
  ReportData,
  SellerProfile,
  SellerSettings,
  ShipmentStatus,
  WhatsAppConfig,
  WhatsAppStatus,
} from '../types';

export const queryKeys = {
  profile: ['profile'] as const,
  stats: ['stats'] as const,
  settings: ['settings'] as const,
  whatsapp: ['whatsapp-status'] as const,
  whatsappConfig: ['whatsapp-config'] as const,
  orders: (status?: OrderStatus | 'all') => ['orders', status ?? 'all'] as const,
  order: (id: string) => ['order', id] as const,
};

export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.get<SellerProfile>('/api/me'),
  });
}

export function useStats() {
  return useQuery({
    queryKey: queryKeys.stats,
    queryFn: () => api.get<DashboardStats>('/api/stats'),
  });
}

export type AttentionKind =
  | 'message_undelivered'
  | 'courier_issue'
  | 'shipment_error'
  | 'conversation_escalated'
  | 'order_blocked';

export interface AttentionItem {
  kind: AttentionKind;
  title: string;
  detail: string;
  orderId?: string;
  conversationId?: string;
  at?: string;
}

export function useNeedsAttention() {
  return useQuery({
    queryKey: ['needs-attention'],
    queryFn: () => api.get<{ count: number; items: AttentionItem[] }>('/api/needs-attention'),
    // Keep it fresh so the badge/count updates as things resolve.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useReport(from?: string, to?: string) {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery({
    queryKey: ['report', from ?? '', to ?? ''],
    queryFn: () => api.get<ReportData>(`/api/reports${suffix}`),
  });
}

export function useOrders(status?: OrderStatus | 'all') {
  const qs = status && status !== 'all' ? `?status=${status}` : '';
  return useQuery({
    queryKey: queryKeys.orders(status),
    queryFn: () => api.get<{ orders: OrderListItem[] }>(`/api/orders${qs}`),
  });
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: queryKeys.order(id),
    queryFn: () => api.get<{ order: OrderListItem }>(`/api/orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useResendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => api.post(`/api/orders/${orderId}/resend`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

/** Pull existing Shopify orders into the dashboard (one-time/manual sync). */
export function useSyncOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ fetched: number; created: number; updated: number }>('/api/orders/sync'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

// ── Settings ──────────────────────────────────────────────────────────────────
export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<{ settings: SellerSettings }>('/api/settings'),
  });
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<SellerSettings>) =>
      api.patch<{ settings: SellerSettings }>('/api/settings', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.settings }),
  });
}

// ── WhatsApp ────────────────────────────────────────────────────────────────
export function useWhatsAppStatus() {
  return useQuery({
    queryKey: queryKeys.whatsapp,
    queryFn: () => api.get<WhatsAppStatus>('/api/whatsapp/status'),
  });
}

/** Meta App ID + Embedded Signup config id for the popup (cached; rarely changes). */
export function useWhatsAppConfig() {
  return useQuery({
    queryKey: queryKeys.whatsappConfig,
    queryFn: () => api.get<WhatsAppConfig>('/api/whatsapp/config'),
    staleTime: 5 * 60_000,
  });
}

export function useConnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { phoneNumberId: string; token: string; businessAccountId?: string }) =>
      api.post<{ status: string; displayPhone: string | null }>('/api/whatsapp/connect', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whatsapp });
      qc.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

/** Finish connection from an Embedded Signup popup result. */
export function useEmbeddedSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmbeddedSignupResult) =>
      api.post<{ status: string; displayPhone: string | null }>(
        '/api/whatsapp/embedded-signup',
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whatsapp });
      qc.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useDisconnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/whatsapp/disconnect'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.whatsapp });
      qc.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useTestWhatsApp() {
  return useMutation({
    mutationFn: (to: string) => api.post<{ ok: boolean }>('/api/whatsapp/test', { to }),
  });
}

/** Begin a QR connection — opens the seller's Baileys session on the backend. */
export function useStartQrConnect() {
  return useMutation({
    mutationFn: () => api.post<QrConnectionState>('/api/whatsapp/qr/start'),
  });
}

/**
 * Poll the live QR/connection state while linking. `enabled` lets the caller
 * start/stop polling; `refetchInterval` keeps the QR + connected transition
 * fresh. On 'connected' the caller should invalidate status/profile.
 */
export function useQrState(enabled: boolean) {
  return useQuery({
    queryKey: ['whatsapp-qr'] as const,
    queryFn: () => api.get<QrConnectionState>('/api/whatsapp/qr'),
    enabled,
    refetchInterval: enabled ? 2000 : false,
  });
}

// ── Couriers ──────────────────────────────────────────────────────────────────
const courierKeys = { list: ['couriers'] as const };

/** Available couriers (by store country) + the seller's connected ones. */
export function useCouriers() {
  return useQuery({
    queryKey: courierKeys.list,
    queryFn: () => api.get<CouriersResponse>('/api/couriers'),
  });
}

export function useConnectCourier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      courier: string;
      apiKey?: string;
      apiPassword?: string;
      pickupAddressCode?: string;
      makeDefault?: boolean;
      testMode?: boolean;
    }) => api.post('/api/couriers/connect', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: courierKeys.list }),
  });
}

export function useDisconnectCourier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courier: string) => api.post('/api/couriers/disconnect', { courier }),
    onSuccess: () => qc.invalidateQueries({ queryKey: courierKeys.list }),
  });
}

/** Manually book an order's shipment with the seller's default courier. */
export function useBookOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => api.post(`/api/couriers/orders/${orderId}/book`),
    onSuccess: (_d, orderId) => {
      qc.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Cancel an order's shipment. */
export function useCancelShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => api.post(`/api/couriers/orders/${orderId}/cancel`),
    onSuccess: (_d, orderId) => qc.invalidateQueries({ queryKey: queryKeys.order(orderId) }),
  });
}

/** TEST MODE only: simulate a courier status update for an order's shipment. */
export function useSimulateShipmentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: ShipmentStatus }) =>
      api.post(`/api/couriers/orders/${orderId}/simulate-status`, { status }),
    onSuccess: (_d, { orderId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.order(orderId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Re-pull the store profile (name/country/currency/timezone) from Shopify. */
export function useResyncStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/settings/resync-store'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.profile });
      qc.invalidateQueries({ queryKey: ['portal', 'stores'] });
      qc.invalidateQueries({ queryKey: courierKeys.list });
    },
  });
}

// ── Onboarding ────────────────────────────────────────────────────────────────
export function useSaveOnboardingPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: {
      country?: string;
      timezone?: string;
      primaryLanguage?: string;
      currency?: string;
    }) => api.post('/api/settings/onboarding/preferences', prefs),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.profile }),
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/settings/onboarding/complete'),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.profile }),
  });
}

// ── Conversations ────────────────────────────────────────────────────────────
export function useConversations() {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.get<{ conversations: ConversationListItem[] }>('/api/conversations'),
    // Near real-time: refresh the inbox every 10s + when the tab regains focus.
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export interface ConversationThread {
  id: string;
  customerPhone: string;
  currentIntent: string;
  aiHandling: boolean;
  outcome: string | null;
  turns: {
    role: 'customer' | 'ai' | 'seller';
    text: string;
    timestamp: string;
    /** Outbound turns only: false = generated but not delivered (e.g. WhatsApp dropped). */
    delivered?: boolean;
    deliveryError?: string | null;
  }[];
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api.get<{ conversation: ConversationThread }>(`/api/conversations/${id}`),
    enabled: Boolean(id),
    // Near real-time: poll the open thread every 5s so new turns appear without
    // a manual reload, plus refresh when the tab regains focus.
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
}

export function useSendReply(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      api.post<{ ok: boolean; reason?: string }>(`/api/conversations/${id}/reply`, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', id] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useSetAiHandling(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aiHandling: boolean) =>
      api.post<{ ok: boolean; aiHandling: boolean }>(`/api/conversations/${id}/ai-handling`, {
        aiHandling,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', id] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ── Templates ────────────────────────────────────────────────────────────────
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ templates: MessageTemplate[] }>('/api/templates'),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      status: OrderStatus;
      language: string;
      body: string;
    }) => api.post<{ template: MessageTemplate }>('/api/templates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<MessageTemplate>) =>
      api.patch<{ template: MessageTemplate }>(`/api/templates/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// ── Order actions ────────────────────────────────────────────────────────────
export function useOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      action,
    }: { orderId: string; action: 'flag-fake' | 'convert-prepaid' | 'unblock' }) =>
      api.post(`/api/orders/${orderId}/${action}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.order(vars.orderId) });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['needs-attention'] });
    },
  });
}
